<template>
  <div class="flex items-center justify-center space-x-2">
    <select-field v-if="week" v-model="_week" :options="weekOptions" :leading="$t('datetime.labels.week')">
      <template #leading>
        <span class="text-sm text-gray-500 dark:text-gray-400 px-3 mr-2 border-r">{{ $t('datetime.labels.week') }}</span>
      </template>
    </select-field>
    <select-field v-if="month" v-model="_month" :options="monthOptions" :leading="$t('datetime.labels.month')">
      <template #leading>
        <span class="text-sm text-gray-500 dark:text-gray-400 px-3 mr-2 border-r">{{ $t('datetime.labels.month') }}</span>
      </template>
    </select-field>
    <select-field v-if="quarter" v-model="_quarter" :options="quarterOptions" :leading="$t('datetime.labels.quarter')">
      <template #leading>
        <span class="text-sm text-gray-500 dark:text-gray-400 px-3 mr-2 border-r">{{ $t('datetime.labels.quarter') }}</span>
      </template>
    </select-field>
    <select-field v-model="_year" :options="yearOptions" :leading="$t('datetime.labels.year')">
      <template #leading>
        <span class="text-sm text-gray-500 dark:text-gray-400 px-3 mr-2 border-r">{{ $t('datetime.labels.year') }}</span>
      </template>
    </select-field>
  </div>
</template>

<script setup lang="ts">
import SelectField from './Select.vue'

const { $auth, $date, $dateFns, $i18n } = useNuxtApp()

interface Props {
  modelValue?: number | string | null
  week?: boolean
  month?: boolean
  quarter?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  week: false,
  month: false,
  quarter: false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
}>()

const _value = computed({
  get () {
    return props.modelValue
  },
  set (value: any) {
    emit('update:modelValue', value)
  },
})

// Composable returns the 0-6 scheme date-fns expects (Sun=0..Sat=6),
// normalised from the location's 1-7 StartDayOfWeek enum.
const weekStartsOn = useCalendarFirstDayOfWeek(() => ($auth as any)?.info?.location?.startDayOfWeek)

const _week = computed({
  get () {
    return ($dateFns as any).getWeek(_value.value, { weekStartsOn: weekStartsOn.value })
  },
  set (value: any) {
    const wso = weekStartsOn.value
    let result = ($dateFns as any).setWeek(_value.value, value, { weekStartsOn: wso })
    result = ($dateFns as any).startOfWeek(result, { weekStartsOn: wso })
    result = ($date as any).add(result, { days: 1 })
    _value.value = ($date as any).getStartOfDayUTC(result)
  },
})

const _month = computed({
  get () {
    return ($dateFns as any).getMonth(_value.value)
  },
  set (value: any) {
    let result = ($dateFns as any).setMonth(_value.value, value)
    result = ($dateFns as any).startOfMonth(result)
    result = ($date as any).add(result, { days: 1 })
    _value.value = ($date as any).getStartOfDayUTC(result)
  },
})

const _quarter = computed({
  get () {
    return ($dateFns as any).getQuarter(_value.value)
  },
  set (value: any) {
    let result = ($dateFns as any).setQuarter(_value.value, value)
    result = ($dateFns as any).startOfQuarter(result)
    result = ($date as any).add(result, { days: 1 })
    _value.value = ($date as any).getStartOfDayUTC(result)
  },
})

const _year = computed({
  get () {
    return ($dateFns as any).getYear(_value.value) - (!props.month && !props.week && !props.quarter ? 1 : 0)
  },
  set (value: any) {
    let result = ($dateFns as any).setYear(_value.value, value + (!props.month && !props.week && !props.quarter ? 1 : 0))
    result = ($dateFns as any).startOfMonth(result)
    result = ($date as any).add(result, { days: 1 })
    _value.value = ($date as any).getStartOfDayUTC(result)
  },
})

const weekOptions = computed(() => {
  const weekStartsOn = 1
  const val = _value.value
  const d = ($dateFns as any).setWeek(val, 1, { weekStartsOn })

  return [...Array(53).keys()].map((key) => {
    const value = ($dateFns as any).setWeek(d, key + 1, { weekStartsOn })
    const format = 'do MMM'

    const start = ($dateFns as any).startOfWeek(value, { weekStartsOn })
    const startLabel = ($date as any).format(start, format)

    const finish = ($dateFns as any).endOfWeek(value, { weekStartsOn })
    const finishLabel = ($date as any).format(finish, format)
    return {
      label: `Week ${key + 1} - ${startLabel} to ${finishLabel}`,
      value: key + 1,
    }
  })
})

const monthOptions = computed(() => {
  return [...Array(12).keys()].map((key) => ({
    label: new Date(0, key).toLocaleString('en', { month: 'long' }),
    value: key,
  }))
})

const quarterOptions = computed(() => [
  { label: ($i18n as any).t('components.fields.period.first'), value: 1 },
  { label: ($i18n as any).t('components.fields.period.second'), value: 2 },
  { label: ($i18n as any).t('components.fields.period.third'), value: 3 },
  { label: ($i18n as any).t('components.fields.period.fourth'), value: 4 },
])

const yearOptions = computed(() => {
  const max = new Date().getFullYear() + 1
  const min = max - 15
  const options: number[] = []

  for (let i = max; i >= min; i--) {
    options.push(i)
  }

  return options
})

watch(() => props.quarter, (value) => {
  if (value) {
    _quarter.value = ($dateFns as any).getQuarter(_value.value)
  }
}, { immediate: true })
</script>
